import {
  IconDefinition,
  faBell,
  faChevronRight,
  faEnvelope,
  faPhone,
  faSms,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import JourneyDetail from './JourneyDetail';
import {
  closedEntries,
  closedHoldout,
  hasEnoughData,
  isStopRateRed,
} from './stats';
import {
  Cell,
  Chevron,
  ErrorText,
  GridRow,
  GroupChip,
  Muted,
  NameButton,
  StepChip,
  SwitchButton,
} from './tableStyles';
import useRowSave from './useRowSave';
import { cadenceSteps, stepWhen } from '../Overview/overviewModel';
import Pill from '../shared/Pill';
import { channelLabel } from '../shared/labels';
import { formatMoney, formatPercent } from '../shared/format';

import PermissionComponent from '@/routes/PermissionComponent';
import {
  JourneyChanges,
  JourneyStep,
  LifecycleJourney,
} from '@/services/lifecycleAPI';

const GROUP_LABELS = {
  profile: 'Profile',
  compliance: 'Compliance',
  activation: 'Activation',
  retention: 'Retention',
};

const CHANNEL_ICONS: Record<string, IconDefinition> = {
  sms: faSms,
  push: faBell,
  email: faEnvelope,
  ops: faPhone,
};

const READ_ONLY_TIP = 'Tenant admin only';
const COPY_PENDING_TIP = 'Copy pending: cannot be switched on';

type Props = {
  journey: LifecycleJourney;
  window: '7d' | '30d';
  isOpen: boolean;
  canEdit: boolean;
  onToggle: () => void;
  onUpdate: (key: string, changes: JourneyChanges) => Promise<void>;
};

const Step = ({ step }: { step: JourneyStep }) => (
  <StepChip title={`${step.key} · ${step.label}`}>
    <strong style={{ fontWeight: 600 }}>{stepWhen(step)}</strong>
    <FontAwesomeIcon
      icon={CHANNEL_ICONS[step.channel] ?? faSms}
      style={{ fontSize: 11 }}
    />
    {channelLabel(step.channel)} · {step.label}
  </StepChip>
);

const JourneyRow = ({
  journey,
  window,
  isOpen,
  canEdit,
  onToggle,
  onUpdate,
}: Props) => {
  const key = journey.journey_key;
  const stats = journey.stats[window];
  const { pending, run, errorFor } = useRowSave(key, onUpdate);
  const paused = journey.sms_paused_at !== null;
  const enoughData = hasEnoughData(stats);
  // graphy's STOP rate is the STOPs that reply to the window's SMS over those
  // SMS (bug hunt 18 BH18-01), so the base is the window's SMS. Red marks the
  // rate on screen once the 7-day count is meaningful (SC-58).
  const stopBase = stats.sms_stop_base ?? stats.messages_sent.sms ?? 0;
  const week = journey.stats['7d'];
  const stopBaseIn7Days = week.sms_stop_base ?? week.messages_sent.sms ?? 0;
  const stopRed = isStopRateRed(stopBaseIn7Days, stats.sms_stop_rate);
  const enableBlocked = journey.copy_pending && !journey.enabled;
  const readOnlyTip = canEdit ? undefined : READ_ONLY_TIP;
  const rowError = errorFor('row');

  return (
    <>
      <GridRow data-testid={`journeyRow-${key}`} open={isOpen}>
        <Chevron
          aria-expanded={isOpen}
          aria-label={`Show details for ${key}`}
          type="button"
          onClick={onToggle}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </Chevron>
        <Cell>
          <GroupChip group={journey.definition.group}>
            {GROUP_LABELS[journey.definition.group]}
          </GroupChip>
          <NameButton type="button" onClick={onToggle}>
            {journey.name}
          </NameButton>
          <Muted>{key}</Muted>
          {journey.copy_pending && (
            <Pill
              title="Product has not approved this copy yet, so the journey cannot be switched on."
              tone="amber"
            >
              Copy pending
            </Pill>
          )}
          {paused && (
            <>
              <Pill tone="amber">
                {journey.sms_pause_reason
                  ? `SMS paused · ${journey.sms_pause_reason}`
                  : 'SMS paused'}
              </Pill>
              <span title={readOnlyTip}>
                <PermissionComponent id="btn-lifecycle-settings" showAsReadOnly>
                  <button
                    disabled={!canEdit || pending}
                    style={{
                      border: 'none',
                      background: 'none',
                      padding: 0,
                      fontSize: 13,
                      fontWeight: 600,
                      color: canEdit ? '#1E8E3E' : '#8A8A8A',
                      cursor: canEdit ? 'pointer' : 'default',
                    }}
                    type="button"
                    onClick={() => run({ clear_sms_pause: true }, 'row')}
                  >
                    Resume SMS
                  </button>
                </PermissionComponent>
              </span>
            </>
          )}
          {rowError && <ErrorText role="alert">{rowError}</ErrorText>}
        </Cell>
        <Cell css={{ fontSize: '13px' }}>
          <span>{journey.definition.enters}</span>
          <Muted>Exits: {journey.definition.exits}</Muted>
        </Cell>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {cadenceSteps(journey.definition.steps).map((step) => (
            <Step key={step.key} step={step} />
          ))}
        </div>
        <div title={enableBlocked ? COPY_PENDING_TIP : readOnlyTip}>
          <PermissionComponent id="btn-lifecycle-settings" showAsReadOnly>
            <SwitchButton
              aria-checked={journey.enabled}
              aria-label={`${key} on`}
              data-testid={`journeyEnabled-${key}`}
              disabled={!canEdit || pending || enableBlocked}
              role="switch"
              title={enableBlocked ? COPY_PENDING_TIP : readOnlyTip}
              type="button"
              onClick={() => run({ enabled: !journey.enabled }, 'row')}
            />
          </PermissionComponent>
        </div>
        <div title={readOnlyTip}>
          <PermissionComponent id="btn-lifecycle-settings" showAsReadOnly>
            <SwitchButton
              aria-checked={journey.sms_enabled && !paused}
              aria-label={`${key} SMS`}
              data-testid={`journeySms-${key}`}
              disabled={!canEdit || pending || paused}
              role="switch"
              title={readOnlyTip}
              type="button"
              onClick={() => run({ sms_enabled: !journey.sms_enabled }, 'row')}
            />
          </PermissionComponent>
        </div>
        <Cell css={{ gap: '2px' }}>
          <strong style={{ fontSize: 16 }}>{stats.active}</strong>
          <Muted>{stats.enrolled} entered</Muted>
          <Muted>Holdout {journey.holdout_percent}%</Muted>
        </Cell>
        <Cell css={{ gap: '2px' }}>
          {enoughData ? (
            <span>
              {`${formatPercent(stats.conversion_journey, 0)} vs ${formatPercent(stats.conversion_holdout, 0)} holdout`}
            </span>
          ) : (
            <span style={{ fontWeight: 600 }}>Not enough data yet</span>
          )}
          <Muted>
            {`n = ${closedEntries(stats)} / ${closedHoldout(stats)}${
              enoughData && stats.p_value !== null
                ? ` · p = ${stats.p_value.toFixed(2)}`
                : ''
            }`}
          </Muted>
        </Cell>
        <Cell css={{ gap: '2px' }}>
          <span
            data-testid={`stopRate-${key}`}
            data-tone={stopRed ? 'red' : 'grey'}
            style={{
              fontWeight: stopRed ? 700 : 600,
              color: stopRed ? '#B0030A' : '#5A5A5A',
            }}
          >
            {stopBase ? formatPercent(stats.sms_stop_rate, 1) : 'No SMS'}
          </span>
          {stopBase > 0 && (
            <Muted>{`${stats.sms_stop_count} of ${stopBase} SMS`}</Muted>
          )}
        </Cell>
        <strong>
          {stats.cost_per_goal_cents === null
            ? '–'
            : formatMoney(stats.cost_per_goal_cents)}
        </strong>
      </GridRow>
      {isOpen && (
        <JourneyDetail
          canEdit={canEdit}
          error={errorFor('detail')}
          journey={journey}
          pending={pending}
          stats={stats}
          windowLabel={window === '30d' ? 'last 30 days' : 'last 7 days'}
          onCancel={onToggle}
          onSave={(changes) => run(changes, 'detail')}
        />
      )}
    </>
  );
};

export default JourneyRow;
